import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';

import ImageService from '../../../services/ImageService';

// Store actions
import { uploadImageUrl, setError, setImageDimensions } from '../../../actions/upload';
import { setCropEndpointCalled, setCropData, setDrawingMode } from '../../../actions/crop';

// App Configs
import appRoutes from '../../../constants/appRoutes';
import {
  DEFAULT,
  NO_ERROR,
} from '../../../constants/errorConstants';

// Common components
import CropToolbar from '../../common/CropToolbar/CropToolbar';

// Upload components
import DragArea from '../DragArea/DragArea';

// Component styles
import './dragAreaContainer.scss';

/**
 * This container is linked to the store `upload` key and it wraps the DragArea and the CropToolbar component.
 * - Update a image url when the image upload is successfull.
 * - Set an error when the image upload process has an error.
 * @extends {Component}
 */
class DragAreaContainer extends React.Component {
  constructor(props) {
    super(props);

    // Context binding for functions.
    this.onImageUploaded = this.onImageUploaded.bind(this);
    this.clearError = this.clearError.bind(this);
  }

  /**
   * This is the callback method that is going to be sent to the `DragArea` component.
   * When the upload is successfull, this method will be triggered in order to dispatch the action that uploads the
   * imageUrl, reset the image crop data and the error if exists. Then redirect to the `crop` page.
   */
  onImageUploaded(imageUrl) {
    this.props.uploadImageUrl(imageUrl);
    this.props.setCropEndpointCalled(false);
    this.props.setCropData([]);

    if (this.props.errorCode) {
      this.props.setError(NO_ERROR);
    }

    ImageService.getImageMetadata(imageUrl)
      .then((metadata) => {
        const { width, height } = metadata;
        this.props.setImageDimensions({ width, height });
        this.props.setDrawingMode(false);
        this.props.redirectToCrop();
      })
      .catch(() => {
        // Any error will display the default error message in the UI.
        this.props.setError(DEFAULT);
      });
  }

  /**
   * This method returns an object with all the DragArea proptypes the component needs.
   */
  getDragAreaProps() {
    return {
      onImageUploaded: this.onImageUploaded,
      onError: this.props.setError,
      errorCode: this.props.errorCode,
      imageUrl: this.props.imageUrl,
      onErrorClose: this.clearError,
    };
  }

  /**
   * Clear store errorCode.
   */
  clearError() {
    this.props.setError(NO_ERROR);
  }

  render() {
    return (
      <div className='wtfDragAreaContainer'>
        <DragArea {...this.getDragAreaProps()} />
        <div className='wtfDragAreaContainer_cropToolbar'>
          <CropToolbar
            isRotateButtonEnabled={false}
            isManualCropButtonActive={false}
            isSearchButtonActive={false}
            showNextButtonEnabledTooltip={false}
            showRotateTooltip={false}
          />
        </div>
      </div>
    );
  }
}

/**
 * Set the DragAreaContainer component props default values.
 * @type {Object}
 */
DragAreaContainer.defaultProps = {
  errorCode: '',
  imageUrl: '',
  uploadImageUrl: () => {},
  setError: () => {},
  setCropEndpointCalled: () => {},
  setCropData: () => {},
  redirectToCrop: () => {},
  setImageDimensions: () => {},
  setDrawingMode: () => {},
};

/**
 * Declare the DragAreaContainer component PropTypes for better validation.
 * @type {Object}
 * @property {String}   errorCode             Error code value.
 * @property {String}   imageUrl              Image url for the crop area.
 * @property {Function} uploadImageUrl        This callback will upload the image url when it is ready.
 * @property {Function} setError              This callback will set an error.
 * @property {Function} setCropEndpointCalled Function that sets the flag telling if the crop endpoint has
 *                                            been called. By setting this to false, the text ID process can
 *                                            begin with the new image.
 * @property {Function} setCropData           This callback will update the crop data in empty when a new image
 *                                            is loaded.
 * @property {Function} redirectToCrop        This callback will redirect to the crop component when necessary.
 * @property {Function} setImageDimensions    Once the upload process is completed, this will be called to save the
 *                                            uploaded image metadata in the store.
 * @property {Function} setDrawingMode        Callback that sets the drawing mode flag in the store.
 */
DragAreaContainer.propTypes = {
  errorCode: PropTypes.string,
  imageUrl: PropTypes.string,
  uploadImageUrl: PropTypes.func,
  setError: PropTypes.func,
  setCropEndpointCalled: PropTypes.func,
  setCropData: PropTypes.func,
  redirectToCrop: PropTypes.func,
  setImageDimensions: PropTypes.func,
  setDrawingMode: PropTypes.func,
};

/**
 * It dispatches store action and returns an object of actions that will be merged with component props.
 * @param   {Object} dispatch bind action creators.
 * @returns {Object}
 */
const mapDispatchToProps = dispatch => ({
  redirectToCrop: () => dispatch(push(appRoutes.crop)),
  uploadImageUrl: imageUrl => dispatch(uploadImageUrl(imageUrl)),
  setError: error => dispatch(setError(error)),
  setCropEndpointCalled: flag => dispatch(setCropEndpointCalled(flag)),
  setCropData: cropData => dispatch(setCropData(cropData)),
  setImageDimensions: metadata => dispatch(setImageDimensions(metadata)),
  setDrawingMode: drawingMode => dispatch(setDrawingMode(drawingMode)),
});

/**
 * Connect component with the store and actions.
 * @type {Object}
 */
export default connect(
  state => ({ errorCode: state.upload.errorCode, imageUrl: state.upload.imageUrl }),
  mapDispatchToProps
)(DragAreaContainer);




// WEBPACK FOOTER //
// ./src/components/Upload/DragAreaContainer/DragAreaContainer.js